import { useLocale } from '@app/core/hooks/useLocale';
import { OpmFirstLastWaterfallDictionaryDto } from '@app/shared/models/contracts/opm-calculation-results-dto';
import styles from '@app/modules/projects/outputs/opm-summary-waterfall/opm-summary-waterfall-erf.module.scss';
import { TransactionType } from '@app/shared/models/contracts/enums/shared-enums';
import React from 'react';
import InstrumentTransactionRow from '../row-components/InstrumentTransactionRow';
import TransactionSummaryRow from '../row-components/TransactionSummaryRow';
import InstrumentDiscountFactorRow from '../row-components/InstrumentDiscountFactorRow';
import IterationLevelRow from '../row-components/IterationLevelRow';
import { SubSectionHeader } from '../row-components/SubSectionHeader';

interface InstrumentInterimCashFlowsMovementsProps {
  titleColSpan: number;
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
  selectedInstrument: string;
  waterfallIterations: number;
  hasInfusions: boolean;
  hasRedemptions: boolean;
  hasCashPay: boolean;
  hasDividends: boolean;
}

const InstrumentOtherCashFlowMovements: React.FC<InstrumentInterimCashFlowsMovementsProps> = ({
  titleColSpan,
  opmWaterfall,
  selectedYear,
  selectedInstrument,
  waterfallIterations,
  hasInfusions,
  hasRedemptions,
  hasCashPay,
  hasDividends,
}: InstrumentInterimCashFlowsMovementsProps) => {
  const { l } = useLocale();
  const hasOtherCashFlows = hasRedemptions || hasCashPay || hasDividends;

  return (
    <>
      {hasOtherCashFlows && (
        <>
          <SubSectionHeader
            title={hasInfusions ? l('_OtherInterimCashFlows') : l('_InterimCashFlows')}
            className={styles['sub-section-header']}
            titleColSpan={titleColSpan}
          />
          {hasRedemptions && (
            <>
              <InstrumentTransactionRow
                opmWaterfall={opmWaterfall}
                selectedYear={selectedYear}
                selectedInstrument={selectedInstrument}
                transactionType={TransactionType.Redemption}
                rowKeyPrefix="redemption"
              />
            </>
          )}
          {hasCashPay && (
            <TransactionSummaryRow
              label="_CashInterest"
              valueKey="totalCashPay"
              opmWaterfall={opmWaterfall}
              selectedInstrument={selectedInstrument}
              selectedYear={selectedYear}
              waterfallIterations={waterfallIterations}
            />
          )}
          {hasDividends && (
            <TransactionSummaryRow
              label="_Dividends"
              valueKey="totalDividends"
              opmWaterfall={opmWaterfall}
              selectedInstrument={selectedInstrument}
              selectedYear={selectedYear}
              waterfallIterations={waterfallIterations}
            />
          )}
          <TransactionSummaryRow
            label="_TotalOtherInterimCashFlows"
            valueKey="totalOtherCashflows"
            opmWaterfall={opmWaterfall}
            selectedInstrument={selectedInstrument}
            selectedYear={selectedYear}
            waterfallIterations={waterfallIterations}
            isBold
          />
          <SubSectionHeader
            title={l('_DiscountFactor')}
            className={styles['sub-section-header']}
            titleColSpan={titleColSpan}
          />
          <IterationLevelRow
            label="_RiskFreeRate"
            valueKey="riskFreeRatePc"
            opmWaterfall={opmWaterfall}
            selectedYear={selectedYear}
            waterfallIterations={waterfallIterations}
            isPercentage
            decimalPoint={2}
          />
          <InstrumentDiscountFactorRow
            opmWaterfall={opmWaterfall}
            selectedYear={selectedYear}
            selectedInstrument={selectedInstrument}
            transactionType={TransactionType.Redemption}
          />
          <InstrumentDiscountFactorRow
            opmWaterfall={opmWaterfall}
            selectedYear={selectedYear}
            selectedInstrument={selectedInstrument}
            transactionType={TransactionType.CashPaid}
          />
          <InstrumentDiscountFactorRow
            opmWaterfall={opmWaterfall}
            selectedYear={selectedYear}
            selectedInstrument={selectedInstrument}
            transactionType={TransactionType.Dividend}
          />
          <TransactionSummaryRow
            label={hasInfusions ? '_TotalPvOfOtherInterimCashFlows' : '_TotalPvOfInterimCashFlows'}
            valueKey="otherCashflowsFairValue"
            opmWaterfall={opmWaterfall}
            selectedInstrument={selectedInstrument}
            selectedYear={selectedYear}
            waterfallIterations={waterfallIterations}
            isBold
          />
        </>
      )}
    </>
  );
};

export default InstrumentOtherCashFlowMovements;
